import { message } from 'antd';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClockContext } from '../app';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import { get_datas, get_state, post_config, post_tracker_stop, post_tracker_start, post_tracker_startsim, post_tracker_setactive } from '../services/api.service';
import './simulator.scss';
import { ApiMoteurData, ApiSimulatorConfig, ApiSimulatorState, ApiTrackerInfos } from '../types';

var g_firsttimestate = true;
/* -------------------------------------------------------------------------------- *\
|                               SimulatorPage
\* -------------------------------------------------------------------------------- */
export const SimulatorPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [loading, setLoading] = useState(true);
    const [listdatas, setDatas] = useState<string[]>([]);
    const [useddatas, setUsedDatas] = useState<string>('');
    const [startat, setStartAt] = useState<number>(0);
    const [speed, setSpeed] = useState<number>(1);

    const [currentdatas, setCurrentDatas] = useState<string>('');
    const [currentstartat, setCurrentStartAt] = useState<number>(0);
    const [currentspeed, setCurrentSpeed] = useState<number>(1);
    const [timestamp, setTimestamp] = useState<number>(0);
    const [running, setRunning] = useState<boolean>(false);
    const [trackers, setTrackers] = useState<ApiTrackerInfos[]>([]);
    const [sortBy, setSortBy] = useState<'number' | 'hardware'>('number');
    const [engineState, setEngineState] = useState<'stop' | 'started' | 'simulationstarted'>('stop');
    const [moteur, setMoteur] = useState<ApiMoteurData | null>(null);
    const [simStartAt, setSimStartAt] = useState<number>(0);

    /* -------------------------------------------------------------------------------- *\
    |                               downloadFusions
    \* -------------------------------------------------------------------------------- */
    function downloadFusions()
    {
        if (!moteur) return;
        const NB_TRACKERS = 5;
        const rows: string[] = [`Number;${Array.from({length: NB_TRACKERS}, (_, i) => `Tracker ${i + 1}`).join(';')}`];
        moteur.liste_fusion
            .map((fusionId, i) => ({ fusionId, ids: moteur.id_fusion[i] ?? [] }))
            .sort((a, b) => a.fusionId - b.fusionId)
            .forEach(({ fusionId, ids }) =>
            {
                const cols = Array.from({length: NB_TRACKERS}, (_, i) => ids[i] !== undefined && ids[i] !== -1 ? ids[i] : '');
                rows.push(`${fusionId};${cols.join(';')}`);
            });

        const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const now = new Date();
        const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const a = document.createElement('a');
        a.href = url;
        a.download = `trackers-${date}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    const clock = useClockContext();

    const navigate = useNavigate();

    /* -------------------------------------------------------------------------------- *\
    |                               changedatas
    \* -------------------------------------------------------------------------------- */
    function changedatas(value: string)
    {
        setUsedDatas(value);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               dochangeconfig
    \* -------------------------------------------------------------------------------- */
    function dochangeconfig()
    {
        const config: ApiSimulatorConfig =
        {
            datas: useddatas,
            start: startat,
            speed: speed,
        };
        setLoading(true);
        post_config(config).then(() =>
        {
            message.info('config changed');
        })
        .catch(error =>
        {
            message.error(String(error));
            if (error.status === 403) navigate('/login');
        })
        .finally(() => setLoading(false));
    }
    /* -------------------------------------------------------------------------------- *\
    |                               refreshDatas
    \* -------------------------------------------------------------------------------- */
    const refreshDatas = async () =>
    {
        get_datas().then((datas: string[]) =>
        {
            setDatas(datas);
        })
        .catch(error =>
        {
            message.error(String(error));
            if (error.status === 403) navigate('/login');
        });
    }
    /* -------------------------------------------------------------------------------- *\
    |                               refresState
    \* -------------------------------------------------------------------------------- */
    const refreshState = async () =>
    {
        get_state().then((state: ApiSimulatorState | null) =>
        {
            if (state)
            {
                setCurrentDatas(state.datas);
                setCurrentStartAt(state.start);
                setCurrentSpeed(state.speed);
                setTimestamp(state.ts);
                setRunning(state.started);
                setTrackers(state.trackers);
                setEngineState(state.engine_state);
                setMoteur(state.moteur);

                if (listdatas.length > 0 && g_firsttimestate)
                {
                    if (!listdatas.includes(state.datas))
                    {
                        state.datas = listdatas[0];
                    }
                    setUsedDatas(state.datas);
                    setStartAt(state.start);
                    setSpeed(state.speed);
                    setSimStartAt(state.tracker_start ?? 0);
                    g_firsttimestate = false;
                }
            }
        })
        .catch(error =>
        {
            message.error(String(error));
            if (error.status === 403) navigate('/login');
        });
    }
    /* -------------------------------------------------------------------------------- *\
    |                               effects
    \* -------------------------------------------------------------------------------- */
    useEffect(() => {
        setLoading(true);
        refreshDatas()
            .then(() => refreshState())
            .then(() => setLoading(false));
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        const interval = setInterval(() =>
        {
            refreshState();
        }, 1000);
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [listdatas]);

    const notrackers = false;
    const sortedTrackers = [...trackers].sort((a, b) => sortBy === 'number' ? a.number - b.number : a.hardware.localeCompare(b.hardware));

    const now = new Date(timestamp);
    const ts = now.toLocaleString('fr-FR', { hour: 'numeric', minute: 'numeric', second: 'numeric' });
    const date = now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return (
        <LayoutSite>
            <div className="simulator">
                {loading ? <Loader /> : <></>}
                <div className="simulator__header">
                    <div>Simulator</div>
                    <div className='calendar__header__clock'>{clock}</div>
                </div>
                <div className="simulator__body">
                    <div className="simulator__body__state">
                        <div className="simulator__body__state__header">current state</div>
                        <div className="simulator__body__state__row">
                            <div className="simulator__body__state__row__label">datas :</div>
                            <div className="simulator__body__state__row__value">{currentdatas}</div>
                        </div>
                        <div className="simulator__body__state__row">
                            <div className="simulator__body__state__row__label">start at :</div>
                            <div className="simulator__body__state__row__value">{currentstartat}</div>
                        </div>
                        <div className="simulator__body__state__row">
                            <div className="simulator__body__state__row__label">speed :</div>
                            <div className="simulator__body__state__row__value">{currentspeed}</div>
                        </div>
                        <div className="simulator__body__state__rowspace"/>
                        <div className="simulator__body__state__row">
                            <div className="simulator__body__state__row__label">running :</div>
                            <div className="simulator__body__state__row__value">{running ? "true" : "false"}</div>
                        </div>
                        <div className="simulator__body__state__row">
                            <div className="simulator__body__state__row__label">date :</div>
                            <div className="simulator__body__state__row__value">{date}</div>
                        </div>
                        <div className="simulator__body__state__row">
                            <div className="simulator__body__state__row__label">TS :</div>
                            <div className="simulator__body__state__row__value">{ts}</div>
                        </div>
                    </div>
                    <div className="simulator__body__datas">
                        <div className="simulator__body__datas__header">parameters</div>
                        <div className="simulator__body__datas__row">
                            <div className="simulator__body__datas__row__label">datas :</div>
                            <div className="simulator__body__datas__row__value">
                                <select onChange={(e) => changedatas(e.target.value)} value={useddatas}>
                                    {
                                        listdatas.map((option, iel) => 
                                        {
                                            return (<option key={`datas_${iel}`} value={option}>{option}</option>)
                                        })
                                    }
                                </select>
                            </div>
                        </div>
                        <div className="simulator__body__datas__row">
                            <div className="simulator__body__datas__row__label">start at :</div>
                            <div className="simulator__body__datas__row__value">
                                <input type="number" value={startat} onChange={(e) => setStartAt(parseInt(e.target.value))} />
                            </div>
                            <div>(seconds)</div>
                        </div>
                        <div className="simulator__body__datas__row">
                            <div className="simulator__body__datas__row__button" onClick={() => dochangeconfig()}>Change</div>
                        </div>
                    </div>
                    {
                        notrackers &&
                    <div className="simulator__body__datas">
                        <div className="simulator__body__datas__header">trackers</div>
                        <div className="simulator__body__state__row">
                            <div className="simulator__body__state__row__label">count :</div>
                            <div className="simulator__body__state__row__value">{trackers.length}</div>
                        </div>
                        <div className="simulator__body__state__row">
                            <div className="simulator__body__state__row__label">état :</div>
                            <div className="simulator__body__state__row__value">{engineState}</div>
                        </div>
                        <div className="simulator__body__datas__row">
                            <div className="simulator__body__datas__row__label">start at :</div>
                            <div className="simulator__body__datas__row__value">
                                <input type="number" value={simStartAt} onChange={(e) => setSimStartAt(parseInt(e.target.value) || 0)} />
                            </div>
                            <div>(seconds)</div>
                        </div>
                        <div className="simulator__body__datas__row">
                                <div className="simulator__trackers__controls">
                                    <div className={`simulator__body__datas__row__button${engineState === 'stop' ? '--active' : ''}`}
                                        onClick={() => post_tracker_stop().catch(e => message.error(String(e)))}>Stop</div>
                                    <div className={`simulator__body__datas__row__button${engineState === 'started' ? '--active' : ''}`}
                                        onClick={() => post_tracker_start().catch(e => message.error(String(e)))}>Start</div>
                                    <div className={`simulator__body__datas__row__button${engineState === 'simulationstarted' ? '--active' : ''}`}
                                        onClick={() => post_tracker_startsim(simStartAt).catch(e => message.error(String(e)))}>Sim</div>
                                </div>
                        </div>
                    </div>
                    }
                </div>
                <div className="simulator__bottom">
                    <div className="simulator__trackers">
                        <div className="simulator__trackers__header">
                            <div className="simulator__trackers__sort">
                                <div className={`simulator__trackers__sort__btn${sortBy === 'number' ? '--active' : ''}`} onClick={() => setSortBy('number')}>id</div>
                                <div className={`simulator__trackers__sort__btn${sortBy === 'hardware' ? '--active' : ''}`} onClick={() => setSortBy('hardware')}>hardware</div>
                            </div>
                        </div>
                        {
                            sortedTrackers.map((t) => (
                            <div key={t.number} className={`simulator__trackers__item${t.active ? '' : '--inactive'}`}
                                onClick={() => post_tracker_setactive(t.number, !t.active).catch(e => message.error(String(e)))}>
                                <div className="simulator__trackers__item__number">#{t.number}</div>
                                <div className="simulator__trackers__item__hardware">{t.hardware}</div>
                            </div>
                            ))
                        }
                    </div>
                    {
                        moteur && (
                        <div className="simulator__fusions">
                            <div className="simulator__fusions__header">
                                <span>fusions</span>
                                <div className="simulator__fusions__button" onClick={downloadFusions}>CSV</div>
                            </div>
                            {moteur.liste_fusion
                                .map((fusionId, i) => ({ fusionId, ids: (moteur.id_fusion[i] ?? []).filter(v => v !== -1) }))
                                .sort((a, b) => a.fusionId - b.fusionId)
                                .map(({ fusionId, ids }) => (
                                    <div key={fusionId} className="simulator__fusions__item">
                                        <div className="simulator__fusions__item__id">#{fusionId}</div>
                                        <div className="simulator__fusions__item__trackers">
                                            {ids.map(id => {
                                                const active = trackers.some(t => t.number === id);
                                                return <span key={id} className={`simulator__fusions__item__tracker${active ? '--active' : ''}`}>{id}</span>;
                                            })}
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                        )
                    }
                </div>
            </div>
        </LayoutSite>
    );
}
